"use strict";
/* ============== Helpers ============== */
Object.defineProperty(exports, "__esModule", { value: true });
exports.composeServiceToContainerConfig = composeServiceToContainerConfig;
exports.composeToContainerConfigs = composeToContainerConfigs;
const parseDockerCompose_1 = require("./parseDockerCompose");
const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
/** Parse a Docker size string like "256m", "1g", "512k", "1024" into bytes */
function parseSizeToBytes(size) {
    var _a;
    if (size == null) {
        return undefined;
    }
    if (typeof size === 'number') {
        return size;
    }
    const match = size.match(/^(\d+(?:\.\d+)?)\s*([kmgtKMGT])?[bB]?$/);
    if (!match) {
        return undefined;
    }
    const value = parseFloat(match[1]);
    switch ((_a = match[2]) === null || _a === void 0 ? void 0 : _a.toLowerCase()) {
        case 'k':
            return Math.round(value * 1024);
        case 'm':
            return Math.round(value * 1024 * 1024);
        case 'g':
            return Math.round(value * 1024 * 1024 * 1024);
        case 't':
            return Math.round(value * 1024 * 1024 * 1024 * 1024);
        default:
            return Math.round(value);
    }
}
function normalizeEnv(env) {
    if (!env) {
        return undefined;
    }
    if (Array.isArray(env)) {
        const out = {};
        for (const item of env) {
            const i = item.indexOf('=');
            if (i === -1) {
                out[item] = '';
            }
            else {
                out[item.slice(0, i)] = item.slice(i + 1);
            }
        }
        return out;
    }
    const out = {};
    for (const [k, v] of Object.entries(env)) {
        out[k] = v;
    }
    return out;
}
// "8080:80/tcp" | "80" | {target:80,published:8080,protocol:'tcp'}
function mapPorts(ports) {
    var _a, _b, _c, _d;
    if (!ports || ports.length === 0) {
        return undefined;
    }
    const out = [];
    for (const p of ports) {
        if (typeof p === 'string') {
            // Parse "<host>[:hostPort]:containerPort[/proto]" OR "containerPort[/proto]"
            const [left, proto] = p.split('/');
            const parts = left.split(':');
            if (parts.length === 1) {
                // "80" - only container port
                const containerPort = Number(parts[0]);
                if (!isNaN(containerPort)) {
                    out.push({ containerPort: containerPort, protocol: (_a = proto) !== null && _a !== void 0 ? _a : 'tcp' });
                }
            }
            else if (parts.length === 2) {
                // "8080:80" - hostPort:containerPort
                const hostPort = Number(parts[0]);
                const containerPort = Number(parts[1]);
                if (!isNaN(hostPort) && !isNaN(containerPort)) {
                    out.push({ hostPort, containerPort, protocol: (_b = proto) !== null && _b !== void 0 ? _b : 'tcp' });
                }
            }
            else if (parts.length === 3) {
                // "192.168.0.1:8080:80" - host:hostPort:containerPort
                const host = parts[0];
                const hostPort = Number(parts[1]);
                const containerPort = Number(parts[2]);
                if (host && !isNaN(hostPort) && !isNaN(containerPort)) {
                    out.push({
                        hostPort,
                        containerPort,
                        protocol: (_c = proto) !== null && _c !== void 0 ? _c : 'tcp',
                        hostIP: host,
                    });
                }
            }
        }
        else {
            out.push({
                hostPort: p.published,
                containerPort: p.target,
                protocol: (_d = p.protocol) !== null && _d !== void 0 ? _d : 'tcp',
            });
        }
    }
    return out;
}
// "hostPath:containerPath:ro" | "volName:containerPath" | object mount
function mapVolumes(vols, globalVolumes) {
    var _a, _b, _c, _d;
    const res = {};
    if (!(vols === null || vols === void 0 ? void 0 : vols.length)) {
        return res;
    }
    const mounts = [];
    const tmpfs = [];
    for (const v of vols) {
        if (typeof v === 'string' && (globalVolumes === null || globalVolumes === void 0 ? void 0 : globalVolumes[v])) {
            // todo
        }
        else if (typeof v === 'string') {
            // parse "src:dst[:mode]"
            const segs = v.split(':');
            if (segs.length >= 2) {
                const [src, dst, mode] = segs;
                mounts.push({
                    type: src.startsWith('/') || src.startsWith('./') || src.startsWith('../') ? 'bind' : 'volume',
                    source: src,
                    target: dst,
                    // can't distinguish bind/volume reliably in short form; omit type
                    readOnly: (mode === null || mode === void 0 ? void 0 : mode.includes('ro')) ? true : undefined,
                });
            }
            else if (segs.length === 1) {
                // anonymous volume: ":/path" not present; keep as volume with only target?
                // Compose a short form with a single path means bind of current dir? Rare; ignore.
            }
        }
        else {
            const m = {
                source: (_a = v.source) !== null && _a !== void 0 ? _a : '',
                target: v.target,
                type: v.type,
                readOnly: (_b = v.read_only) !== null && _b !== void 0 ? _b : undefined,
            };
            if (v.type === 'tmpfs') {
                tmpfs.push({ target: v.target, size: (_c = v.tmpfs) === null || _c === void 0 ? void 0 : _c.size, mode: (_d = v.tmpfs) === null || _d === void 0 ? void 0 : _d.mode });
            }
            else {
                // If the source is missing for a named anonymous volume, keep an empty string (manager can resolve)
                mounts.push(m);
            }
        }
    }
    if (mounts.length) {
        res.mounts = mounts;
    }
    if (tmpfs.length) {
        res.tmpfs = tmpfs;
    }
    return res;
}
function duration2ms(d) {
    if (d == null) {
        return undefined;
    }
    if (typeof d === 'number') {
        return d;
    }
    // crude parser: supports 1h, 5m, 30s; if plain number, assume seconds
    const m = String(d).match(/^(\d+)(ns|us|ms|s|m|h)?$/);
    if (!m) {
        return undefined;
    }
    const n = Number(m[1]);
    const u = m[2] || 's';
    switch (u) {
        case 'h':
            return n * 3600000;
        case 'm':
            return n * 60000;
        case 's':
            return n * 1000;
        case 'ms':
            return n;
        case 'ns':
            return Math.ceil(n / 1000000);
        case 'us':
            return Math.ceil(n / 1000);
        default:
            return undefined;
    }
}
function mapDevices(devs) {
    if (!devs || devs.length === 0) {
        return undefined;
    }
    const out = [];
    for (const d of devs) {
        if (typeof d === 'string') {
            // "/dev/video0:/dev/video0:rwm" or "/dev/video0"
            const segs = d.split(':');
            if (segs.length === 1) {
                out.push({ hostPath: segs[0] });
            }
            else if (segs.length === 2) {
                out.push({ hostPath: segs[0], containerPath: segs[1] });
            }
            else {
                out.push({ hostPath: segs[0], containerPath: segs[1], permissions: segs[2] });
            }
        }
        else {
            out.push({
                hostPath: d.source,
                containerPath: d.target,
                permissions: d.permissions,
            });
        }
    }
    return out;
}
function mapExtraHosts(e) {
    if (!e) {
        return undefined;
    }
    if (Array.isArray(e)) {
        return e.slice();
    }
    const out = [];
    for (const [host, ip] of Object.entries(e)) {
        out.push({ host, ip });
    }
    return out;
}
function mapDNS(svc) {
    var _a, _b, _c;
    const servers = ((_a = svc.dns) === null || _a === void 0 ? void 0 : _a.length) ? svc.dns : undefined;
    const search = ((_b = svc.dns_search) === null || _b === void 0 ? void 0 : _b.length) ? svc.dns_search : undefined;
    const options = ((_c = svc.dns_opt) === null || _c === void 0 ? void 0 : _c.length) ? svc.dns_opt : undefined;
    if (!servers && !search && !options) {
        return undefined;
    }
    return { servers, search, options };
}
function mapHealthcheck(h) {
    if (!h) {
        return undefined;
    }
    return {
        test: h.test,
        interval: duration2ms(h.interval),
        timeout: duration2ms(h.timeout),
        retries: h.retries,
        startPeriod: duration2ms(h.start_period),
    };
}
function mapRestart(r) {
    if (!r) {
        return undefined;
    }
    if (r.startsWith('on-failure')) {
        const parts = r.split(':');
        const maxRetries = parts.length > 1 ? parseInt(parts[1], 10) : undefined;
        return {
            policy: 'on-failure',
            maxRetries: maxRetries && !isNaN(maxRetries) ? maxRetries : undefined,
        };
    }
    return {
        policy: r,
    };
}
function parseDurationToSeconds(d) {
    if (!d) {
        return undefined;
    }
    // crude parser: supports 1h, 5m, 30s; if plain number, assume seconds
    const m = String(d).match(/^(\d+)(ns|us|ms|s|m|h)?$/);
    if (!m) {
        return undefined;
    }
    const n = Number(m[1]);
    const u = m[2] || 's';
    switch (u) {
        case 'h':
            return n * 3600;
        case 'm':
            return n * 60;
        case 's':
            return n;
        case 'ms':
            return Math.ceil(n / 1000);
        default:
            return undefined; // ignore ns/us
    }
}
function normalizeLabels(labels) {
    if (!labels) {
        return undefined;
    }
    if (Array.isArray(labels)) {
        const out = {};
        for (const l of labels) {
            const i = l.indexOf('=');
            if (i === -1) {
                out[l] = '';
            }
            else {
                out[l.slice(0, i)] = l.slice(i + 1);
            }
        }
        return out;
    }
    const out = {};
    for (const [k, v] of Object.entries(labels)) {
        out[k] = String(v);
    }
    return out;
}
function mapNetworks(n, globalNetworks) {
    if (!n) {
        return undefined;
    }
    const out = [];
    if (Array.isArray(n)) {
        for (const item of n) {
            if (typeof item === 'string' && (globalNetworks === null || globalNetworks === void 0 ? void 0 : globalNetworks[item])) {
                // Just network name, and defined in global networks
                // todo
            }
            else if (typeof item === 'string') {
                out.push({ name: item });
            }
            else {
                out.push({
                    name: item.name,
                    aliases: item.aliases,
                    ipv4Address: item.ipv4_address,
                    ipv6Address: item.ipv6_address,
                });
            }
        }
    }
    else if (isObject(n)) {
        // Map string-map form: { netA: {}, netB: { aliases: [...] } }
        for (const [name, cfg] of Object.entries(n)) {
            if (cfg == null) {
                out.push({ name });
            }
            else if (isObject(cfg)) {
                out.push({
                    name,
                    aliases: cfg.aliases,
                    ipv4Address: cfg.ipv4_address,
                    ipv6Address: cfg.ipv6_address,
                });
            }
            else {
                out.push({ name });
            }
        }
    }
    return out.length ? out : undefined;
}
/* ============== Main converter ============== */
function composeServiceToContainerConfig(serviceName, compose) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const svc = (_a = compose.services) === null || _a === void 0 ? void 0 : _a[serviceName || ''];
    if (!svc) {
        throw new Error(`Service ${serviceName} not found in compose file`);
    }
    const env = normalizeEnv(svc.environment);
    // Replace all true/false and numbers with strings, as docker API expects strings
    if (env) {
        for (const k of Object.keys(env)) {
            if (typeof env[k] === 'boolean') {
                env[k] = env[k] ? 'true' : 'false';
            }
            else if (typeof env[k] === 'number') {
                env[k] = String(env[k]);
            }
        }
    }
    const ports = mapPorts(svc.ports);
    const { mounts, tmpfs } = mapVolumes(svc.volumes, compose.volumes);
    const devices = mapDevices(svc.devices);
    const extraHosts = mapExtraHosts(svc.extra_hosts);
    const dns = mapDNS(svc);
    const healthcheck = mapHealthcheck(svc.healthcheck);
    const restart = mapRestart(svc.restart);
    const labels = normalizeLabels(svc.labels);
    const networks = mapNetworks(svc.networks, compose.networks);
    // Build mapping
    let build;
    if (svc.build) {
        if (typeof svc.build === 'string') {
            build = { context: svc.build };
        }
        else if (isObject(svc.build)) {
            build = {
                context: svc.build.context,
                dockerfile: svc.build.dockerfile,
                args: svc.build.args,
                target: svc.build.target,
                cacheFrom: svc.build.cache_from,
                labels: normalizeLabels(svc.build.labels),
            };
        }
    }
    const labelsWithoutIobSettings = Object.assign({}, labels);
    // remove iob labels from normal labels
    labelsWithoutIobSettings === null || labelsWithoutIobSettings === void 0 ? true : delete labelsWithoutIobSettings.iobEnabled;
    labelsWithoutIobSettings === null || labelsWithoutIobSettings === void 0 ? true : delete labelsWithoutIobSettings.iobStopOnUnload;
    labelsWithoutIobSettings === null || labelsWithoutIobSettings === void 0 ? true : delete labelsWithoutIobSettings.iobAutoImageUpdate;
    labelsWithoutIobSettings === null || labelsWithoutIobSettings === void 0 ? true : delete labelsWithoutIobSettings.iobMonitoringEnabled;
    labelsWithoutIobSettings === null || labelsWithoutIobSettings === void 0 ? true : delete labelsWithoutIobSettings.iobBackup;
    labelsWithoutIobSettings === null || labelsWithoutIobSettings === void 0 ? true : delete labelsWithoutIobSettings.iobCopyVolumes;
    labelsWithoutIobSettings === null || labelsWithoutIobSettings === void 0 ? true : delete labelsWithoutIobSettings.iobWaitForReady;
    const iobBackup = (_b = labels === null || labels === void 0 ? void 0 : labels.iobBackup) === null || _b === void 0 ? void 0 : _b.split(',').map(m => m.trim());
    if (iobBackup === null || iobBackup === void 0 ? void 0 : iobBackup.length) {
        mounts === null || mounts === void 0 ? void 0 : mounts.forEach(m => {
            const source = m.source === true ? 'true' : m.source;
            if (source && iobBackup.includes(source)) {
                m.iobBackup = true;
            }
        });
    }
    const iobCopyVolumes = (_c = labels === null || labels === void 0 ? void 0 : labels.iobCopyVolumes) === null || _c === void 0 ? void 0 : _c.split(',').map(m => {
        var _a;
        let force = false;
        if (m.includes('(force)')) {
            m = m.replace('(force)', '');
            force = true;
        }
        const parts = m.includes('=>') ? m.trim().split('=>') : m.trim().split('->');
        return {
            source: parts[0].trim(),
            target: ((_a = parts[1]) === null || _a === void 0 ? void 0 : _a.trim()) || parts[0].trim(),
            force,
        };
    });
    if (iobCopyVolumes) {
        mounts === null || mounts === void 0 ? void 0 : mounts.forEach(m => {
            const source = m.source === true ? 'true' : m.source;
            const copyInstruction = iobCopyVolumes.find(c => c.target === source);
            if (copyInstruction) {
                m.iobAutoCopyFrom = copyInstruction.source;
                if (copyInstruction.force) {
                    m.iobAutoCopyFromForce = copyInstruction.force;
                }
            }
        });
    }
    const cfg = {
        // the following attributes could be a string or boolean in labels; convert to boolean or undefined
        iobEnabled: labels === null || labels === void 0 ? void 0 : labels.iobEnabled, // default true
        iobStopOnUnload: labels === null || labels === void 0 ? void 0 : labels.iobStopOnUnload, // default false
        iobAutoImageUpdate: labels === null || labels === void 0 ? void 0 : labels.iobAutoImageUpdate, // default false
        iobMonitoringEnabled: labels === null || labels === void 0 ? void 0 : labels.iobMonitoringEnabled, // default false
        iobWaitForReady: (labels === null || labels === void 0 ? void 0 : labels.iobWaitForReady) === 'true' ? true : (labels === null || labels === void 0 ? void 0 : labels.iobWaitForReady) === 'false' ? false : undefined, // default false
        name: svc.container_name || serviceName,
        image: svc.image || (build ? `${serviceName}:latest` : 'image:latest'),
        // runtime basics
        command: svc.command,
        entrypoint: svc.entrypoint,
        user: svc.user,
        workdir: svc.working_dir,
        hostname: svc.hostname,
        domainname: svc.domainname,
        // some composition variants use mac_address
        macAddress: svc.mac_address,
        environment: env,
        envFile: svc.env_file ? (Array.isArray(svc.env_file) ? svc.env_file : [svc.env_file]) : undefined,
        labels: labelsWithoutIobSettings,
        tty: svc.tty,
        stdinOpen: svc.stdin_open,
        ports,
        expose: svc.expose,
        mounts,
        devices,
        extraHosts,
        dns,
        networks,
        healthcheck,
        restart,
        logging: svc.logging
            ? {
                driver: svc.logging.driver,
                options: svc.logging.options,
            }
            : undefined,
        security: {
            privileged: svc.privileged,
            apparmor: (_e = (_d = svc.security_opt) === null || _d === void 0 ? void 0 : _d.find(o => o.startsWith('apparmor='))) === null || _e === void 0 ? void 0 : _e.slice(9),
            seccomp: (_g = (_f = svc.security_opt) === null || _f === void 0 ? void 0 : _f.find(o => o.startsWith('seccomp='))) === null || _g === void 0 ? void 0 : _g.slice(8),
            noNewPrivileges: (_h = svc.security_opt) === null || _h === void 0 ? void 0 : _h.includes('no-new-privileges'),
        },
        sysctls: svc.sysctls,
        dependsOn: svc.depends_on,
        stop: {
            gracePeriodSec: parseDurationToSeconds(svc.stop_grace_period),
            signal: svc.stop_signal,
        },
        tmpfs,
        readOnly: svc.read_only,
        resources: parseSizeToBytes(svc.shm_size) !== undefined ? { shmSize: parseSizeToBytes(svc.shm_size) } : undefined,
        build,
    };
    if (cfg.iobEnabled === 'false') {
        cfg.iobEnabled = false;
    }
    else if (cfg.iobEnabled === 'true') {
        cfg.iobEnabled = true;
    }
    if (cfg.iobStopOnUnload === 'false') {
        cfg.iobStopOnUnload = false;
    }
    else if (cfg.iobStopOnUnload === 'true') {
        cfg.iobStopOnUnload = true;
    }
    if (cfg.iobAutoImageUpdate === 'false') {
        cfg.iobAutoImageUpdate = false;
    }
    else if (cfg.iobAutoImageUpdate === 'true') {
        cfg.iobAutoImageUpdate = true;
    }
    if (cfg.iobMonitoringEnabled === 'false') {
        cfg.iobMonitoringEnabled = false;
    }
    else if (cfg.iobMonitoringEnabled === 'true') {
        cfg.iobMonitoringEnabled = true;
    }
    return (0, parseDockerCompose_1.cleanUndefined)(cfg);
}
function composeToContainerConfigs(compose) {
    const res = [];
    if (!compose.services) {
        return res;
    }
    for (const name of Object.keys(compose.services)) {
        res.push(composeServiceToContainerConfig(name, compose));
    }
    return res;
}
